/**
 * Production-ready WebSocket Service for Django Channels
 * Handles connection management, reconnection, authentication, and message handling
 */

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'wss://api.eaquahealth.com';

class SocketService {
    constructor() {
        this.socket = null;
        this.isConnected = false;
        this.userId = null;
        this.caseId = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000;
        this.maxReconnectDelay = 30000;
        this.messageQueue = [];
        this.messageHandlers = new Set();
        this.connectionListeners = new Set();
        this.reconnectTimer = null;
        this.pingInterval = null;
        this.lastPong = null;
    }

    /**
     * Get authentication token from storage
     */
    getAuthToken() {
        try {
            return localStorage.getItem('token') || localStorage.getItem('access_token');
        } catch (error) {
            console.error('Error getting auth token:', error);
            return null;
        }
    }

    /**
     * Build WebSocket URL with authentication
     */
    buildUrl(caseId) {
        const token = this.getAuthToken();
        // Ensure URL uses ws/wss protocol
        let baseUrl = SOCKET_URL;
        if (baseUrl.startsWith('https://')) {
            baseUrl = baseUrl.replace('https://', 'wss://');
        } else if (baseUrl.startsWith('http://')) {
            baseUrl = baseUrl.replace('http://', 'ws://');
        } else if (!baseUrl.startsWith('ws://') && !baseUrl.startsWith('wss://')) {
            baseUrl = 'wss://' + baseUrl;
        }
        
        let url = `${baseUrl}/ws/case/${caseId}/`;
        
        if (token) {
            url += `?token=${encodeURIComponent(token)}`;
        }
        
        return url;
    }

    /**
     * Connect to the WebSocket server for a specific case
     * @param {string} userId - User ID for identification
     * @param {string} caseId - Case ID to connect to (optional, for case chat)
     */
    connect(userId, caseId = null) {
        return new Promise((resolve, reject) => {
            // Prevent multiple connections to same case
            if (this.socket && this.socket.readyState === WebSocket.OPEN && this.caseId === caseId) {
                console.log('WebSocket already connected to case:', caseId);
                resolve(this.socket);
                return;
            }

            // Close existing connection if different case
            if (this.socket && this.caseId !== caseId) {
                this.disconnect();
            }

            this.userId = userId;
            this.caseId = caseId;

            try {
                const url = this.buildUrl(caseId);
                console.log('🔌 Connecting to WebSocket:', url);
                
                this.socket = new WebSocket(url);
                
                this.socket.onopen = () => {
                    console.log('✅ WebSocket connected');
                    this.isConnected = true;
                    this.reconnectAttempts = 0;
                    this.reconnectDelay = 1000;
                    
                    // Start ping/pong for connection health
                    this.startPingInterval();
                    
                    // Process queued messages
                    this.processMessageQueue();
                    
                    // Notify listeners
                    this.notifyConnectionListeners(true);
                    
                    resolve(this.socket);
                };

                this.socket.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        this.handleMessage(data);
                    } catch (error) {
                        console.error('Error parsing message:', error);
                    }
                };

                this.socket.onclose = (event) => {
                    console.log('🔌 WebSocket closed:', event.code, event.reason);
                    this.isConnected = false;
                    this.stopPingInterval();
                    this.notifyConnectionListeners(false);
                    
                    // Attempt reconnection for abnormal closures
                    if (event.code !== 1000 && event.code !== 1001) {
                        this.attemptReconnect();
                    }
                };

                this.socket.onerror = (error) => {
                    console.error('❌ WebSocket error:', error);
                    this.notifyConnectionListeners(false, error);
                    reject(error);
                };

            } catch (error) {
                console.error('Failed to create WebSocket:', error);
                reject(error);
            }
        });
    }

    /**
     * Handle incoming messages
     */
    handleMessage(data) {
        const { type } = data;

        switch (type) {
            case 'message':
                // Notify all message handlers
                this.messageHandlers.forEach(handler => {
                    try {
                        handler(data);
                    } catch (error) {
                        console.error('Error in message handler:', error);
                    }
                });
                break;

            case 'typing':
                // Broadcast typing indicator
                this.messageHandlers.forEach(handler => {
                    if (handler.onTyping) {
                        handler.onTyping(data);
                    }
                });
                break;

            case 'read':
                // Handle read receipts
                this.messageHandlers.forEach(handler => {
                    if (handler.onRead) {
                        handler.onRead(data);
                    }
                });
                break;

            case 'user_joined':
            case 'user_left':
                console.log(`User ${type}:`, data.username);
                break;

            case 'pong':
                this.lastPong = Date.now();
                break;

            case 'error':
                console.error('Server error:', data.message);
                break;

            default:
                console.log('Unknown message type:', type, data);
        }
    }

    /**
     * Send a message through WebSocket
     */
    send(data) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            console.log('Socket not connected, queuing message');
            this.messageQueue.push(data);
            return false;
        }

        try {
            this.socket.send(JSON.stringify(data));
            return true;
        } catch (error) {
            console.error('Error sending message:', error);
            this.messageQueue.push(data);
            return false;
        }
    }

    /**
     * Send a chat message
     */
    sendMessage(caseId, message, options = {}) {
        return new Promise((resolve, reject) => {
            const messageData = {
                type: 'message',
                id: options.id || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                content: message.content || message,
                sender_type: message.sender_type || options.senderType || 'user',
                attachments: message.attachments || options.attachments || [],
                timestamp: new Date().toISOString()
            };

            const sent = this.send(messageData);
            
            if (sent) {
                resolve(messageData);
            } else {
                // Message queued, will be sent when reconnected
                resolve({ ...messageData, queued: true });
            }
        });
    }

    /**
     * Emit typing indicator (alias for compatibility)
     */
    emitTyping(caseId, isTyping = true) {
        this.send({
            type: 'typing',
            is_typing: isTyping
        });
    }

    /**
     * Send read receipt for a message
     */
    sendReadReceipt(messageId) {
        this.send({
            type: 'read',
            message_id: messageId
        });
    }

    /**
     * Subscribe to case messages
     * @param {string} caseId - Case ID to subscribe to
     * @param {function} callback - Callback for new messages
     * @returns {function} Unsubscribe function
     */
    subscribeToCase(caseId, callback) {
        // Store the handler
        this.messageHandlers.add(callback);

        // If not connected to this case, connect
        if (!this.isConnected || this.caseId !== caseId) {
            this.connect(this.userId, caseId).catch(error => {
                console.error('Failed to connect to case:', error);
            });
        }

        // Return unsubscribe function
        return () => {
            this.messageHandlers.delete(callback);
        };
    }

    /**
     * Process queued messages after reconnection
     */
    processMessageQueue() {
        while (this.messageQueue.length > 0) {
            const message = this.messageQueue.shift();
            this.send(message);
        }
    }

    /**
     * Attempt to reconnect with exponential backoff
     */
    attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached');
            this.notifyConnectionListeners(false, new Error('Max reconnection attempts reached'));
            return;
        }

        this.reconnectAttempts++;
        const delay = Math.min(
            this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
            this.maxReconnectDelay
        );

        console.log(`🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

        this.reconnectTimer = setTimeout(() => {
            this.connect(this.userId, this.caseId).catch(error => {
                console.error('Reconnection failed:', error);
            });
        }, delay);
    }

    /**
     * Start ping interval for connection health
     */
    startPingInterval() {
        this.stopPingInterval();
        this.pingInterval = setInterval(() => {
            if (this.isConnected) {
                this.send({ type: 'ping' });
                
                // Check if we received a pong recently
                if (this.lastPong && Date.now() - this.lastPong > 60000) {
                    console.warn('No pong received, connection may be stale');
                }
            }
        }, 30000); // Ping every 30 seconds
    }

    /**
     * Stop ping interval
     */
    stopPingInterval() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    /**
     * Register a connection state listener
     */
    onConnectionChange(callback) {
        this.connectionListeners.add(callback);
        
        // Immediately notify of current state
        callback(this.isConnected);
        
        return () => {
            this.connectionListeners.delete(callback);
        };
    }

    /**
     * Notify all connection listeners
     */
    notifyConnectionListeners(connected, error = null) {
        this.connectionListeners.forEach(listener => {
            try {
                listener(connected, error);
            } catch (e) {
                console.error('Error in connection listener:', e);
            }
        });
    }

    /**
     * Check if connected
     */
    isSocketConnected() {
        return this.isConnected && this.socket?.readyState === WebSocket.OPEN;
    }

    /**
     * Manually reconnect
     */
    reconnect() {
        if (this.userId && this.caseId) {
            this.connect(this.userId, this.caseId);
        }
    }

    /**
     * Disconnect from WebSocket
     */
    disconnect() {
        console.log('🔌 Disconnecting WebSocket');
        
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        
        this.stopPingInterval();
        
        if (this.socket) {
            this.socket.close(1000, 'User disconnected');
            this.socket = null;
        }
        
        this.isConnected = false;
        this.caseId = null;
        this.messageHandlers.clear();
    }

    /**
     * Get current connection state
     */
    getConnectionState() {
        return {
            isConnected: this.isConnected,
            caseId: this.caseId,
            userId: this.userId,
            queuedMessages: this.messageQueue.length
        };
    }
}

// Create singleton instance
const socketService = new SocketService();

// Export both the instance and the class
export { SocketService };
export default socketService;